import React, { useState } from 'react';
import { DataService } from '../../services/dataService';
import type { IMonthItem } from '../../models/IMonthItem';
import type {
  DropdownProps,
  TableColumnDefinition,
} from '@fluentui/react-components';
import { useMsal } from '@azure/msal-react';
import {
  makeStyles,
  tokens,
  Button,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  DrawerHeaderTitle,
  Drawer,
  DrawerBody,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Field,
  DataGrid,
  DataGridBody,
  DataGridRow,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridCell,
  TableCellLayout,
  createTableColumn,
  Spinner,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import {
  ArrowClockwise20Regular,
  Calendar20Regular,
  Delete20Regular,
  Dismiss24Regular,
  DocumentPdfRegular,
  MoreHorizontal20Regular,
  TableFreezeRowRegular,
} from '@fluentui/react-icons';
import GenerateMonth from './generateMonth';
import EditMonth from './editMonth';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import type { IDataItem } from '../../models/IDataItem';

const useStyles = makeStyles({
  container: {
    padding: `0 ${tokens.spacingHorizontalL}`,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  gridContainer: {
    marginTop: '16px',
    overflowX: 'auto',
    overflowY: 'auto',
    flex: 1,
  },
  dateFilterRow: {
    display: 'flex',
    gap: '16px',
    marginTop: '8px',
    marginBottom: '16px',
    flexShrink: 0,
  },
  toolbarButton: {
    backgroundColor: 'white',
    color: 'black',
    ':hover': {
      backgroundColor: '#f0f0f0',
      color: 'black',
    },
  },
});

const onFormatDate = (d?: Date): string => {
  const year = new Intl.DateTimeFormat('de', { year: 'numeric' }).format(d);
  const month = new Intl.DateTimeFormat('de', { month: '2-digit' }).format(d);
  const day = new Intl.DateTimeFormat('de', { day: '2-digit' }).format(d);
  return `${day}.${month}.${year}`;
};

const ROLE_STORAGE_KEY = 'userPersonalRole';
const PERSONAL_PARTS_STORAGE_KEY = 'userPersonalParts';

const getCurrentRole = (): string[] => {
  try {
    const raw = sessionStorage.getItem(ROLE_STORAGE_KEY);
    console.log('DetailView getCurrentRole - raw sessionStorage:', raw);
    if (!raw) return [];

    const parsed = JSON.parse(raw) as string[];

    if (Array.isArray(parsed)) {
      return parsed.map((r) => r.trim());
    }

    return [];
  } catch (error) {
    console.log('DetailView Error parsing role:', error);
    return [];
  }
};

const getPersonalParts = (): string[] => {
  try {
    const raw = sessionStorage.getItem(PERSONAL_PARTS_STORAGE_KEY);
    console.log('DetailView getPersonalParts - raw sessionStorage:', raw);
    if (!raw) return [];

    const parsed = JSON.parse(raw) as string[];

    if (Array.isArray(parsed)) {
      return parsed.map((r) => r.trim());
    }

    return [];
  } catch (error) {
    console.log('DetailView Error parsing personal parts:', error);
    return [];
  }
};

const AllTimetracking: React.FC = () => {
  const STORAGE_FILTER_KEY = 'allTimetrackingFilter';
  const styles = useStyles();
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const dataService = React.useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);
  const [roles, setRoles] = useState<string[]>(getCurrentRole());
  const [personalParts, setPersonalParts] =
    useState<string[]>(getPersonalParts());
  const [auswahlMonthBegin, setAuswahlMonthBegin] = useState<
    Date | null | undefined
  >(null);
  const [auswahlMonthEnd, setAuswahlMonthEnd] = useState<
    Date | null | undefined
  >(new Date());
  const [mitarbeiterId, setMitarbeiterId] = useState([] as string[]);
  const [status, setStatus] = useState([] as string[]);
  const [isLoading, setIsLoading] = useState(false);
  const [auswahlMitarbeiter, setAuswahlMitarbeiter] = useState(
    [] as IDataItem[]
  );
  const [auswahlStatus] = useState<IDataItem[]>([
    { value: 'Approved', text: 'Genehmigt' },
    { value: 'Requested off', text: 'Beantragt' },
    { value: 'Rejected', text: 'Abgelehnt' },
  ] as IDataItem[]);

  const [monthItems, setMonthItems] = useState<IMonthItem[]>([]);

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedMonth, setSelectedMonth] = useState<IMonthItem | null>(null);
  const [selectedMitarbeiterGuid, setSelectedMitarbeiterGuid] = useState<
    string | null
  >(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [dateValidationError, setDateValidationError] = useState<string>('');
  const isInitialMount = React.useRef(true);

  // Validate date range
  React.useEffect(() => {
    if (auswahlMonthBegin && auswahlMonthEnd) {
      if (auswahlMonthBegin > auswahlMonthEnd) {
        setDateValidationError(
          'Bis Monat muss größer oder gleich Von Monat sein'
        );
      } else {
        setDateValidationError('');
      }
    } else {
      setDateValidationError('');
    }
  }, [auswahlMonthBegin, auswahlMonthEnd]);

  React.useEffect(() => {
    const handleRoleChange = () => {
      const newRoles = getCurrentRole();
      setRoles(newRoles);
      const newPersonalParts = getPersonalParts();
      setPersonalParts(newPersonalParts);
    };

    window.addEventListener('role-changed', handleRoleChange);
    window.addEventListener('storage', handleRoleChange);

    return () => {
      window.removeEventListener('role-changed', handleRoleChange);
      window.removeEventListener('storage', handleRoleChange);
    };
  }, [roles]);

  const isWriter =
    roles.some(
      (r) =>
        r.toLowerCase() === 'Admin'.toLowerCase() ||
        r.toLowerCase() === 'Personal'.toLowerCase()
    ) ||
    personalParts.some(
      (p) => p.toLowerCase() === 'timetracking_write'.toLowerCase()
    );

  const saveFiltersToStorage = () => {
    const filterState = {
      auswahlMonthBegin,
      auswahlMonthEnd,
      mitarbeiterId,
      status,
    };
    localStorage.setItem(STORAGE_FILTER_KEY, JSON.stringify(filterState));
  };

  const loadFiltersFromStorage = () => {
    try {
      const savedFilters = localStorage.getItem(STORAGE_FILTER_KEY);
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters);
        setAuswahlMonthBegin(
          parsedFilters.auswahlMonthBegin
            ? new Date(parsedFilters.auswahlMonthBegin)
            : null
        );
        setAuswahlMonthEnd(
          parsedFilters.auswahlMonthEnd
            ? new Date(parsedFilters.auswahlMonthEnd)
            : null
        );
        setMitarbeiterId(parsedFilters.mitarbeiterId || []);
        setStatus(parsedFilters.status || []);
        return true;
      }
    } catch (error) {
      console.error('Error loading filters from localStorage:', error);
    }
    return false;
  };

  React.useEffect(() => {
    loadFiltersFromStorage();
  }, []);

  React.useEffect(() => {
    // Skip saving on initial mount to avoid overwriting loaded values
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (auswahlMonthBegin === null && auswahlMonthEnd === null) {
      localStorage.removeItem(STORAGE_FILTER_KEY);
      return;
    }

    saveFiltersToStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auswahlMonthBegin, auswahlMonthEnd, mitarbeiterId, status]);

  React.useEffect(() => {
    const fetchItems = async () => {
      if (!dataService) return;
      if (!auswahlMonthBegin || !auswahlMonthEnd) return;
      if (auswahlMonthBegin > auswahlMonthEnd) return;
      const mitarbeiterResponse = await dataService.getMitarbeiterAuswahlliste(
        auswahlMonthBegin,
        auswahlMonthEnd
      );
      if (mitarbeiterResponse) setAuswahlMitarbeiter(mitarbeiterResponse);
    };
    fetchItems();
  }, [dataService, auswahlMonthBegin, auswahlMonthEnd]);

  const fetchMonthItems = async () => {
    if (!dataService) return;
    if (!auswahlMonthBegin || !auswahlMonthEnd) return;
    if (auswahlMonthBegin > auswahlMonthEnd) return;
    setIsLoading(true);

    const response = await dataService.getTimetrackingMonths(
      auswahlMonthBegin,
      auswahlMonthEnd,
      mitarbeiterId.length > 0 ? mitarbeiterId[0] : '',
      status.length > 0 ? status[0] : ''
    );
    if (response) setMonthItems(response);
    setIsLoading(false);
  };

  const onOptionSelect = React.useCallback<
    NonNullable<DropdownProps['onOptionSelect']>
  >((_, data) => {
    setMitarbeiterId(data.selectedOptions);
  }, []);

  const onOptionStateSelect = React.useCallback<
    NonNullable<DropdownProps['onOptionSelect']>
  >((_, data) => {
    setStatus(data.selectedOptions);
  }, []);

  // Group months by year
  const columns: TableColumnDefinition<IMonthItem>[] = [
    createTableColumn<IMonthItem>({
      columnId: 'actions',
      renderHeaderCell: () => '',
      renderCell: (item) => (
        <TableCellLayout>
          {isWriter && (
            <Dialog>
              <DialogTrigger disableButtonEnhancement>
                <Button
                  aria-label="Remove item"
                  appearance="subtle"
                  icon={<Delete20Regular />}
                />
              </DialogTrigger>
              <DialogSurface
                aria-labelledby={`${item.itemId.toString()}-title`}
                aria-describedby={`${item.itemId.toString()}-content`}
              >
                <DialogBody>
                  <DialogTitle id={`${item.itemId.toString()}-title`}>
                    Monat löschen
                  </DialogTitle>
                  <DialogContent id={`${item.itemId.toString()}-content`}>
                    Wollen Sie den Monat {item.strMonat} wirklich löschen?
                  </DialogContent>
                  <DialogActions>
                    <DialogTrigger disableButtonEnhancement>
                      <Button
                        appearance="primary"
                        onClick={() => handleDeleteMonth(item)}
                      >
                        Monat löschen
                      </Button>
                    </DialogTrigger>
                    <DialogTrigger disableButtonEnhancement>
                      <Button appearance="secondary">Abbrechen</Button>
                    </DialogTrigger>
                  </DialogActions>
                </DialogBody>
              </DialogSurface>
            </Dialog>
          )}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                aria-label="Options"
                appearance="subtle"
                icon={<MoreHorizontal20Regular />}
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => handleShowMonth(item)}>Show</MenuItem>
                {isWriter && (
                  <MenuItem onClick={() => handleEditMonth(item)}>
                    Edit
                  </MenuItem>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>
        </TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'jahr',
      compare: (a, b) => a.jahr - b.jahr,
      renderHeaderCell: () => 'Jahr',
      renderCell: (item) => <TableCellLayout>{item.jahr}</TableCellLayout>,
    }),
    createTableColumn<IMonthItem>({
      columnId: 'monat',
      compare: (a, b) => a.monat - b.monat,
      renderHeaderCell: () => 'Monat',
      renderCell: (item) => <TableCellLayout>{item.strMonat}</TableCellLayout>,
    }),
    createTableColumn<IMonthItem>({
      columnId: 'abteilung',
      compare: (a, b) => (a.abteilung || '').localeCompare(b.abteilung || ''),
      renderHeaderCell: () => 'Abteilung',
      renderCell: (item) => <TableCellLayout>{item.abteilung}</TableCellLayout>,
    }),
    createTableColumn<IMonthItem>({
      columnId: 'fullName',
      compare: (a, b) => a.fullName.localeCompare(b.fullName),
      renderHeaderCell: () => 'Name',
      renderCell: (item) => <TableCellLayout>{item.fullName}</TableCellLayout>,
    }),
    createTableColumn<IMonthItem>({
      columnId: 'personalNummer',
      compare: (a, b) => a.personalNummer.localeCompare(b.personalNummer),
      renderHeaderCell: () => 'Personalnummer',
      renderCell: (item) => (
        <TableCellLayout>{item.personalNummer}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'strEintrittsDatum',
      renderHeaderCell: () => 'Eintritt',
      compare: (a, b) => {
        const dateA = a.eintrittsDatum ? new Date(a.eintrittsDatum) : null;
        const dateB = b.eintrittsDatum ? new Date(b.eintrittsDatum) : null;
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateA.getTime() - dateB.getTime();
      },
      renderCell: (item) => (
        <TableCellLayout>{item.strEintrittsDatum}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'wochenStunden',
      compare: (a, b) => a.wochenStunden - b.wochenStunden,
      renderHeaderCell: () => 'Wochenstunden',
      renderCell: (item) => (
        <TableCellLayout>{item.wochenStunden}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'urlaubsanspruch',
      compare: (a, b) => a.urlaubsanspruch - b.urlaubsanspruch,
      renderHeaderCell: () => 'Urlaubsanspruch',
      renderCell: (item) => (
        <TableCellLayout>{item.urlaubsanspruch}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'urlaubVormonat',
      compare: (a, b) => a.urlaubVormonat - b.urlaubVormonat,
      renderHeaderCell: () => 'Urlaubsstand Vormonat',
      renderCell: (item) => (
        <TableCellLayout>{item.urlaubVormonat}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'urlaubVorKarenz',
      compare: (a, b) => a.urlaubVorKarenz - b.urlaubVorKarenz,
      renderHeaderCell: () => 'Urlaub vor Karenz',
      renderCell: (item) => (
        <TableCellLayout>{item.urlaubVorKarenz}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'verbrauchterUrlaub',
      compare: (a, b) => a.verbrauchterUrlaub - b.verbrauchterUrlaub,
      renderHeaderCell: () => 'Verbrauchter Urlaub',
      renderCell: (item) => (
        <TableCellLayout>{item.verbrauchterUrlaub}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'urlaubsRest',
      compare: (a, b) => a.urlaubsRest - b.urlaubsRest,
      renderHeaderCell: () => 'Urlaub Ende des Monats',
      renderCell: (item) => (
        <TableCellLayout>{item.urlaubsRest}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'ueberStundenVormonat',
      compare: (a, b) => a.ueberStundenVormonat - b.ueberStundenVormonat,
      renderHeaderCell: () => 'Überstunden Vormonat',
      renderCell: (item) => (
        <TableCellLayout>{item.ueberStundenVormonat}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'ueberStundenVorKarenz',
      compare: (a, b) => a.ueberStundenVorKarenz - b.ueberStundenVorKarenz,
      renderHeaderCell: () => 'Überstunden vor Karenz',
      renderCell: (item) => (
        <TableCellLayout>{item.ueberStundenVorKarenz}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'monatsUeberschuss',
      compare: (a, b) => a.monatsUeberschuss - b.monatsUeberschuss,
      renderHeaderCell: () => 'Monatsüberschuss',
      renderCell: (item) => (
        <TableCellLayout>{item.monatsUeberschuss}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'ueberStundenVorAbzug',
      compare: (a, b) => a.ueberStundenVorAbzug - b.ueberStundenVorAbzug,
      renderHeaderCell: () => 'Überstunden Ende des Monats',
      renderCell: (item) => (
        <TableCellLayout>{item.ueberStundenVorAbzug}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'ueberStundenAbzug',
      compare: (a, b) => a.ueberStundenAbzug - b.ueberStundenAbzug,
      renderHeaderCell: () => 'Abzug Überstunden',
      renderCell: (item) => (
        <TableCellLayout>{item.ueberStundenAbzug}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'ueberStunden',
      compare: (a, b) => a.ueberStunden - b.ueberStunden,
      renderHeaderCell: () => 'Überstunden nach Abzug',
      renderCell: (item) => (
        <TableCellLayout>{item.ueberStunden}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'gleitzeitKonto',
      compare: (a, b) => a.gleitzeitKonto - b.gleitzeitKonto,
      renderHeaderCell: () => 'Gleitzeitkonto',
      renderCell: (item) => (
        <TableCellLayout>{item.gleitzeitKonto}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'labWorkStunden',
      compare: (a, b) => a.labWorkStunden - b.labWorkStunden,
      renderHeaderCell: () => 'Lab Work',
      renderCell: (item) => (
        <TableCellLayout>{item.labWorkStunden}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'krankenTage',
      compare: (a, b) => a.krankenTage - b.krankenTage,
      renderHeaderCell: () => 'Krankentage',
      renderCell: (item) => (
        <TableCellLayout>{item.krankenTage}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'pflegeTage',
      compare: (a, b) => a.pflegeTage - b.pflegeTage,
      renderHeaderCell: () => 'Pflegeurlaub',
      renderCell: (item) => (
        <TableCellLayout>{item.pflegeTage}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'arztbesuche',
      compare: (a, b) => a.arztbesuche - b.arztbesuche,
      renderHeaderCell: () => 'Arztbesuche',
      renderCell: (item) => (
        <TableCellLayout>{item.arztbesuche}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'kommentar',
      renderHeaderCell: () => 'Kommentar',
      renderCell: (item) => <TableCellLayout>{item.kommentar}</TableCellLayout>,
    }),
    createTableColumn<IMonthItem>({
      columnId: 'teleworkingTage',
      compare: (a, b) => a.teleworkingTage - b.teleworkingTage,
      renderHeaderCell: () => 'Teleworking Tage',
      renderCell: (item) => (
        <TableCellLayout>{item.teleworkingTage}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'workflowStatus',
      renderHeaderCell: () => 'Status',
      renderCell: (item) => (
        <TableCellLayout>{item.workflowStatus}</TableCellLayout>
      ),
    }),
    createTableColumn<IMonthItem>({
      columnId: 'strCreated',
      renderHeaderCell: () => 'Erstellungsdatum',
      renderCell: (item) => (
        <TableCellLayout>{item.strCreated}</TableCellLayout>
      ),
    }),
  ];

  const handleShowMonth = (month: IMonthItem) => {
    const date = new Date();
    date.setFullYear(month.jahr, month.monat - 1, 1);
    setSelectedDate(date);
    setSelectedMonth(month);
    setSelectedMitarbeiterGuid(month.mitarbeiterGuid);
    setIsDrawerOpen(true);
  };

  const handleAddMonth = () => {
    setSelectedMonth(null);
    setSelectedDate(new Date());
    setSelectedMitarbeiterGuid(
      mitarbeiterId.length > 0 ? mitarbeiterId[0] : ''
    );
    setIsDrawerOpen(true);
  };

  const handleEditMonth = (month: IMonthItem) => {
    setSelectedMonth(month);
    setSelectedDate(new Date(month.jahr, month.monat - 1, 1));
    setIsEditDrawerOpen(true);
  };

  const handleDeleteMonth = async (month: IMonthItem) => {
    if (!dataService) return;
    if (!auswahlMonthBegin || !auswahlMonthEnd) return;
    if (auswahlMonthBegin > auswahlMonthEnd) return;
    await dataService.deleteMonthItem(month.itemId);
    const response = await dataService.getTimetrackingMonths(
      auswahlMonthBegin,
      auswahlMonthEnd,
      mitarbeiterId.length > 0 ? mitarbeiterId[0] : '',
      status.length > 0 ? status[0] : ''
    );
    if (response) setMonthItems(response);
  };

  const handleDrawerClose = async () => {
    setSelectedMitarbeiterGuid(null);
    setSelectedDate(new Date());
    setSelectedMonth(null);
    setIsEditDrawerOpen(false);
    setIsDrawerOpen(false);
  };

  const downloadMonthPdf = async () => {
    if (!dataService) return;
    if (!auswahlMonthBegin || !auswahlMonthEnd) return;
    if (auswahlMonthBegin > auswahlMonthEnd) return;
    if (mitarbeiterId.length === 0) return;
    setIsLoading(true);
    const str = await dataService.getAllMonthCompletionPdf(
      auswahlMonthBegin,
      auswahlMonthEnd,
      mitarbeiterId[0]
    );

    const url = window.URL.createObjectURL(
      new Blob([str], {
        type: str.type,
      })
    );
    setIsLoading(false);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Monatsliste.pdf`;
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadMonthExcel = async () => {
    if (!dataService) return;
    if (!auswahlMonthBegin || !auswahlMonthEnd) return;
    if (auswahlMonthBegin > auswahlMonthEnd) return;
    if (mitarbeiterId.length === 0) return;
    setIsLoading(true);
    const base64 = await dataService.getAllMonthCompletionExcel(
      auswahlMonthBegin,
      auswahlMonthEnd,
      mitarbeiterId[0]
    );
    setIsLoading(false);
    if (base64 != null) {
      const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64}`;
      const downloadLink = document.createElement('a');
      const fileName = 'Monatsuebersicht.xlsx';
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
    }
  };

  const downloadMonthListeExcel = async () => {
    if (!dataService) return;
    if (!auswahlMonthBegin || !auswahlMonthEnd) return;
    if (auswahlMonthBegin > auswahlMonthEnd) return;
    setIsLoading(true);

    const base64 = await dataService.getTimetrackingMonthsExcel(
      auswahlMonthBegin,
      auswahlMonthEnd,
      mitarbeiterId.length > 0 ? mitarbeiterId[0] : '',
      status.length > 0 ? status[0] : ''
    );
    setIsLoading(false);
    if (base64 != null) {
      const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64}`;
      const downloadLink = document.createElement('a');
      const fileName = 'Monatslisten.xlsx';
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
    }
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.dateFilterRow}>
          <Field
            label="Von Monat"
            validationState={dateValidationError ? 'error' : 'none'}
          >
            <DatePicker
              placeholder="Select a date..."
              disabled={isLoading}
              formatDate={onFormatDate}
              value={auswahlMonthBegin}
              onSelectDate={setAuswahlMonthBegin}
            />
          </Field>
          <Field
            label="Bis Monat"
            validationState={dateValidationError ? 'error' : 'none'}
            validationMessage={dateValidationError}
          >
            <DatePicker
              placeholder="Select a date..."
              disabled={isLoading}
              formatDate={onFormatDate}
              value={auswahlMonthEnd}
              onSelectDate={setAuswahlMonthEnd}
            />
          </Field>
        </div>
        <div className={styles.dateFilterRow}>
          <Dropdown
            title="Filtern nach Person"
            clearable
            disabled={isLoading}
            selectedOptions={mitarbeiterId}
            value={
              mitarbeiterId.length > 0
                ? mitarbeiterId
                    .map(
                      (id) =>
                        auswahlMitarbeiter.find((t) => t.value === id)?.text ||
                        id
                    )
                    .join(', ')
                : ''
            }
            onOptionSelect={onOptionSelect}
            style={{ width: '350px' }}
          >
            {auswahlMitarbeiter.map((t) => (
              <Option key={t.value} value={t.value}>
                {t.text}
              </Option>
            ))}
          </Dropdown>
          <Dropdown
            title="Filtern nach Status"
            clearable
            disabled={isLoading}
            selectedOptions={status}
            value={
              status.length > 0
                ? status
                    .map(
                      (id) =>
                        auswahlStatus.find((t) => t.value === id)?.text || id
                    )
                    .join(', ')
                : ''
            }
            onOptionSelect={onOptionStateSelect}
            style={{ width: '200px' }}
          >
            {auswahlStatus.map((t) => (
              <Option key={t.value} value={t.value}>
                {t.text}
              </Option>
            ))}
          </Dropdown>
          <Button
            appearance="primary"
            disabled={
              isLoading ||
              !auswahlMonthBegin ||
              !auswahlMonthEnd ||
              dateValidationError !== ''
            }
            icon={<ArrowClockwise20Regular />}
            onClick={fetchMonthItems}
          >
            Aktualisieren
          </Button>
          {monthItems.length > 0 && !isLoading && (
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button
                  aria-label="Options"
                  appearance="subtle"
                  icon={<MoreHorizontal20Regular />}
                />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  {isWriter && (
                    <MenuItem
                      icon={<Calendar20Regular />}
                      onClick={handleAddMonth}
                    >
                      Monat hinzufügen
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={downloadMonthPdf}
                    icon={<DocumentPdfRegular />}
                  >
                    PDF Export
                  </MenuItem>
                  <MenuItem
                    onClick={downloadMonthExcel}
                    icon={<TableFreezeRowRegular />}
                  >
                    Excel Export
                  </MenuItem>
                  <MenuItem
                    onClick={downloadMonthListeExcel}
                    icon={<TableFreezeRowRegular />}
                  >
                    Liste Excel Export
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          )}
        </div>
        {isLoading && (
          <Spinner label="Loading..." style={{ marginTop: '10px' }} />
        )}
        {!isLoading && (
          <>
            {monthItems.length > 0 ? (
              <div className={styles.gridContainer}>
                <DataGrid
                  items={monthItems}
                  columns={columns}
                  sortable
                  resizableColumns
                  focusMode="composite"
                  style={{ minWidth: '550px' }}
                  columnSizingOptions={{
                    actions: { minWidth: 80, defaultWidth: 80 },
                    jahr: { minWidth: 80, defaultWidth: 80 },
                    monat: { minWidth: 100, defaultWidth: 100 },
                    abteilung: { minWidth: 150, defaultWidth: 200 },
                    fullName: { minWidth: 150, defaultWidth: 250 },
                    personalNummer: { minWidth: 120, defaultWidth: 140 },
                    strEintrittsDatum: { minWidth: 100, defaultWidth: 120 },
                    wochenStunden: { minWidth: 100, defaultWidth: 130 },
                    urlaubsanspruch: { minWidth: 120, defaultWidth: 150 },
                    urlaubVormonat: { minWidth: 150, defaultWidth: 180 },
                    urlaubVorKarenz: { minWidth: 120, defaultWidth: 160 },
                    verbrauchterUrlaub: { minWidth: 130, defaultWidth: 170 },
                    urlaubsRest: { minWidth: 150, defaultWidth: 200 },
                    ueberStundenVormonat: { minWidth: 150, defaultWidth: 180 },
                    ueberStundenVorKarenz: { minWidth: 150, defaultWidth: 200 },
                    monatsUeberschuss: { minWidth: 130, defaultWidth: 160 },
                    ueberStundenVorAbzug: { minWidth: 180, defaultWidth: 220 },
                    ueberStundenAbzug: { minWidth: 140, defaultWidth: 180 },
                    ueberStunden: { minWidth: 160, defaultWidth: 200 },
                    gleitzeitKonto: { minWidth: 120, defaultWidth: 140 },
                    labWorkStunden: { minWidth: 100, defaultWidth: 120 },
                    krankenTage: { minWidth: 100, defaultWidth: 130 },
                    pflegeTage: { minWidth: 100, defaultWidth: 130 },
                    arztbesuche: { minWidth: 100, defaultWidth: 120 },
                    kommentar: { minWidth: 150, defaultWidth: 200 },
                    homeofficeTage: { minWidth: 130, defaultWidth: 160 },
                    workflowStatus: { minWidth: 100, defaultWidth: 120 },
                    strCreated: { minWidth: 140, defaultWidth: 170 },
                  }}
                >
                  <DataGridHeader>
                    <DataGridRow>
                      {({ renderHeaderCell }) => (
                        <DataGridHeaderCell>
                          {renderHeaderCell()}
                        </DataGridHeaderCell>
                      )}
                    </DataGridRow>
                  </DataGridHeader>
                  <DataGridBody<IMonthItem>>
                    {({ item, rowId }) => (
                      <DataGridRow<IMonthItem>
                        key={rowId}
                        onDoubleClick={() => handleShowMonth(item)}
                      >
                        {({ renderCell }) => (
                          <DataGridCell>{renderCell(item)}</DataGridCell>
                        )}
                      </DataGridRow>
                    )}
                  </DataGridBody>
                </DataGrid>
              </div>
            ) : (
              <p
                style={{
                  fontSize: '16px',
                  marginBottom: '30px',
                  maxWidth: '500px',
                }}
              >
                Keine Monate gefunden.
              </p>
            )}
          </>
        )}
      </div>
      <Drawer
        size="full"
        open={isDrawerOpen}
        onOpenChange={(_, { open }) => {
          if (!open) {
            handleDrawerClose();
          } else {
            setIsDrawerOpen(true);
          }
        }}
        position="end"
      >
        <DrawerHeaderTitle
          style={{ paddingLeft: '15px' }}
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={() => handleDrawerClose()}
            />
          }
        >
          {selectedMonth?.strMonat !== undefined
            ? `${selectedMonth.strMonat} - ${selectedMonth.name}`
            : 'Select month'}
        </DrawerHeaderTitle>
        <DrawerBody>
          <GenerateMonth
            mitarbeiterGuid={selectedMitarbeiterGuid || ''}
            selectedMonth={selectedMonth}
            selectedDate={selectedDate}
            onClose={handleDrawerClose}
          />
        </DrawerBody>
      </Drawer>
      <Drawer
        size="medium"
        open={isEditDrawerOpen}
        onOpenChange={(_, { open }) => {
          if (!open) {
            handleDrawerClose();
          } else {
            setIsEditDrawerOpen(true);
          }
        }}
        position="end"
      >
        <DrawerHeaderTitle
          style={{ paddingLeft: '15px' }}
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={() => handleDrawerClose()}
            />
          }
        >
          {selectedMonth?.strMonat !== undefined
            ? `${selectedMonth.strMonat} - ${selectedMonth.fullName}`
            : 'No month selected'}
        </DrawerHeaderTitle>
        <DrawerBody>
          <EditMonth
            selectedMonth={selectedMonth}
            onClose={handleDrawerClose}
          />
        </DrawerBody>
      </Drawer>
    </>
  );
};

export default AllTimetracking;
